<template>
  <div>
    <div
      class="add-buttons"
      :class="{ 'show-add-buttons': displayAddButtons }"
      @mouseenter="showAddButtons"
      @mouseleave="hideAddButtons">
      <div
        class="add-multiple-button"
        :class="{ 'add-multiple-button-show': filesAreProcessing || displayAddMultiplePopup }"
        @click="showAddMultiple">
        <i class="mdi mdi-plus-box-multiple mdi-24px" />
      </div>
      <div
        class="add-note-button"
        @click="$emit('add-notes')">
        <i class="mdi mdi-text mdi-24px" />
      </div>
      <div
        class="add-button"
        @click="$emit('add-card')">
        <i class="mdi mdi-plus mdi-36px" />
      </div>
    </div>

    <b-modal
      v-model="displayAddMultiplePopup"
      has-modal-card>
      <div class="modal-card">
        <header class="modal-card-head">
          <p class="modal-card-title">
            Add multiple files
          </p>
          <button
            type="button"
            class="delete"
            @click="displayAddMultiplePopup = false" />
        </header>
        <section class="modal-card-body">
          <b-upload
            v-show="!(selectedFiles.length + processedFiles.length)"
            v-model="selectedFiles"
            @input="processFiles"
            multiple
            drag-drop
            expanded
            accept=".pdf,.docx,.xlsx,.pptx,.png,.jpg">
            <section class="section">
              <div class="content has-text-centered">
                <p>
                  <b-icon icon="upload" size="is-large" />
                </p>
                <p>
                  Drop your files here or click to upload
                </p>
              </div>
            </section>
          </b-upload>
          <div
            class="selected-files"
            v-show="selectedFiles.length + processedFiles.length">
            <div
              v-for="file in processedFiles"
              :key="file.cardId"
              class="processed-file"
              @click="goToFinishedCard({ id: file.cardId })">
              <i class="mdi mdi-check" /> {{file.name}}
            </div>
            <div
              v-for="(file, index) in selectedFiles"
              :key="index"
              class="selected-file" >
              <i class="mdi mdi-loading mdi-spin" /> {{file.name}}
            </div>
          </div>
        </section>
        <footer class="modal-card-foot">
          <button
            class="button"
            type="button"
            @click="closeAddMultiplePopup">
            {{ (selectedFiles.length + processedFiles.length) ? 'Close' : 'Cancel' }}
          </button>
          <button
            v-show="!filesAreProcessing && processedFiles.length"
            class="button"
            type="button"
            @click="processedFiles = []">
            Add more files
          </button>
        </footer>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';

export default {
  name: 'AddButtons',
  data() {
    return {
      displayAddMultiplePopup: false,
      selectedFiles: [],
      filesAreProcessing: false,
      processedFiles: [],
      newCardIds: [],
      displayAddButtons: false,
      hideAddButtonsTimer: null,
    };
  },
  computed: {
    ...mapState({
      selectedCollection: state => state.collections.selectedCollection,
      introStep: state => state.appState.introStep,
    }),
  },
  methods: {
    ...mapActions({
      getNewCardIds: 'cards/getNewCardIds',
      persistAll: 'sync/persistAll',
    }),
    showAddButtons() {
      clearTimeout(this.hideAddButtonsTimer);
      this.displayAddButtons = true;
    },
    hideAddButtons() {
      this.hideAddButtonsTimer = setTimeout(() => { this.displayAddButtons = false }, 300); //eslint-disable-line
    },
    closeAddMultiplePopup() {
      // If we're done processing files, empty the array holding them
      if (!this.filesAreProcessing && this.processedFiles.length) this.processedFiles.length = 0;
      this.displayAddMultiplePopup = false;
    },
    showAddMultiple() {
      this.displayAddMultiplePopup = true;
      this.processedFiles.length = 0;
    },
    processFiles() {
      this.filesAreProcessing = true;
      const number = this.selectedFiles.length;

      // If a new card is added to a just created collection, the collection may still have a temporary id (negative number). In that case, we cannot request a new card id due to
      // FK constraints in the database (requires existing collection). So check for negative collectionId, and only continue once we have one.
      if (this.selectedCollection < 0) {
        const unsubscribe = this.$store.subscribe((mutation) => {
          let newCollectionId = null;
          if (mutation.type === 'collections/ReplaceNewCollectionId') newCollectionId = mutation.payload.newId;
          else if (newCollectionId && mutation.type === 'collections/SelectCollection') {
            this.uploadFiles(number);
            unsubscribe();
          }
        });
      }
      else this.uploadFiles(number);
    },
    async uploadFiles(number) {
      // Get ids and store them (array)
      const ids = await this.getNewCardIds({ number, relatedCollectionId: this.selectedCollection });

      // Subscribe for Vuex mutations that will result from the later part of this function
      const unsubscribe = this.$store.subscribe((mutation) => {
        const { type, payload } = mutation;

        // Check if a file is done uploading
        if (type === 'cards/UpdateCard' && payload.field === 'item' && ids.includes(payload.cardRef.id)) {
          const index = this.selectedFiles.findIndex(file => file.name === payload.data.address);
          this.selectedFiles.splice(index, 1);

          // Move to 'finished' array to display results in modal
          this.processedFiles.push({
            name: payload.cardRef.title,
            cardId: payload.cardRef.id,
          });

          // If all are uploaded
          if (!this.selectedFiles.length) {
            this.filesAreProcessing = false;
            // Sync, to be sure
            this.persistAll({});

            // If the user closed the upload modal, show a notification that we're done
            if (!this.displayAddMultiplePopup) {
              this.$buefy.toast.open({
                message: 'All files are uploaded!',
                type: 'is-secondary-dark',
                duration: 2000,
              });
              // Also empty the relevant array so that we can upload new ones (see v-show condition in modal)
              this.processedFiles.length = 0;
            }
            unsubscribe();
          }
        }
      });

      for (let i = 0; i < number; i++) {
        // Get data, update title, upload file (which updates item)
        const file = this.selectedFiles[i];
        const fileData = new FormData();
        fileData.append('card-item', file);
        const { name: filename } = file;
        const title = filename.slice(0, filename.lastIndexOf('.'));
        const id = ids[i];

        this.$updateCard({
          id,
          data: {
            title,
            dateLastModified: this.$getCurrentUTC(),
          },
        });

        this.$store.dispatch('files/uploadFile', {
          cardId: ids[i],
          relatedCollectionId: this.selectedCollection,
          filename,
          fileData,
          file,
        });
      }
    },
    goToFinishedCard({ id }) {
      this.displayAddMultiplePopup = false;
      this.$goToCard({ id });
    },
  },
};
</script>

<style lang="scss" scoped>
@import '~styles/colors';

.add-buttons {
  position: fixed;
  bottom: 1.5rem;
  right: 1.5rem;
  height: 3rem;
  width: 3rem;
  z-index: 7;
  transition: height 0.3s;
}
.show-add-buttons {
  height: 10.5rem;
  transition: height 0.3s;
}
.show-add-buttons .add-multiple-button,
.show-add-buttons .add-note-button {
  opacity: 0.75;
  bottom: 9rem;
  transition: bottom 0.15rem;
}
.show-add-buttons .add-note-button {
  bottom: 5.25rem;
}
.show-add-buttons:hover .add-button {
  opacity: 0.75;
}
.add-button,
.add-note-button,
.add-multiple-button {
  position: fixed;
  bottom: 1.5rem;
  right: 1.5rem;
  height: 3rem;
  width: 3rem;
  display: flex;
  justify-content: center;
  align-items: center;
  color: $white;
  box-shadow: 0.5px 0.5px 2px 1px $primary;
  border-radius: 50%;
  background-color: $secondary-dark;
  z-index: 5;
  cursor: pointer;
  transition: opacity 0.3s;
}
.add-multiple-button,
.add-note-button {
  line-height: 3rem;
  padding: 0 calc(1.5rem - 12px); // half of width container - half of width icon
  z-index: 4;
  opacity: 0.5;
  bottom: 1.5rem;
  transition: bottom 0.3s;
}
.add-button:hover,
.add-multiple-button:hover,
.add-note-button:hover {
  opacity: 1 !important;
}
.add-multiple-button-show {
  opacity: 0.75;
}
.selected-files {
  height: auto;
  max-height: 30vh;
  overflow-y: auto;
  overflow-x: hidden;
}
.selected-file,
.processed-file {
  width: 30vw;
  margin: 0.15rem auto;
  background: $secondary-light;
  padding: 0.25rem;
  font-size: 0.85rem;
  border-radius: 0.25rem;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
.processed-file {
  background: $secondary-dark;
  color: $primary-light;
  cursor: pointer;
}
.modal-card-body,
.modal-card-foot {
  display: flex;
  justify-content: center;
}
.modal-card-body .section {
  padding: 4vh 1.5rem;
}
.modal-card-body {
  ::v-deep .upload-draggable {
    overflow: hidden;
  }
}
</style>
